import { Campaign, SAVE_VERSION, type CampaignSave } from '@core/campaign/campaign';
import type { Bathymetry } from '@core/map/bathymetry';
import type { Locale } from '@i18n/index';

/**
 * Spielstand im Browserspeicher.
 *
 * Bewusst genau ein Platz: Das Spiel handelt von Entscheidungen, die weh tun.
 * Mehrere Staende einzuladen wuerde dazu einladen, eine misslungene Rettung
 * zurueckzunehmen — und damit genau das aushebeln, worum es geht.
 */

const SAVE_KEY = 'bermuda.save.v1';
const SETTINGS_KEY = 'bermuda.settings.v1';

export interface Settings {
  readonly locale: Locale;
  readonly muted: boolean;
  /**
   * Vergroesserungsfaktor der Schrift.
   *
   * Optional, damit Einstellungen aus einer Fassung vor M7 gueltig bleiben —
   * anders als beim Spielstand waere es hier unhoeflich, jemandem seine
   * Sprachwahl wegen eines neuen Feldes zu loeschen.
   */
  readonly textScale?: number;
}

function storage(): Storage | null {
  try {
    // In einem privaten Fenster kann der Zugriff werfen statt null zu liefern.
    return window.localStorage;
  } catch {
    return null;
  }
}

export function hasSave(): boolean {
  return storage()?.getItem(SAVE_KEY) !== null && storage()?.getItem(SAVE_KEY) !== undefined;
}

export function saveCampaign(campaign: Campaign): void {
  const store = storage();
  if (store === null) return;

  try {
    store.setItem(SAVE_KEY, JSON.stringify(campaign.serialise()));
  } catch (error) {
    // Voller Speicher darf das Spiel nicht anhalten.
    console.warn('Spielstand konnte nicht gesichert werden:', error);
  }
}

export function loadCampaign(
  bathymetry: Bathymetry,
  coastline: readonly (readonly (readonly [number, number])[])[] = [],
): Campaign | null {
  const raw = storage()?.getItem(SAVE_KEY);
  if (raw === null || raw === undefined) return null;

  try {
    const save = JSON.parse(raw) as CampaignSave;
    // Aeltere Faelle werden verworfen statt migriert — der Aufwand lohnt sich
    // erst, wenn es Spieler gibt, deren Staende erhalten bleiben muessen.
    if (save.version !== SAVE_VERSION) return null;
    return Campaign.restore(save, bathymetry, coastline);
  } catch (error) {
    console.warn('Spielstand ist unlesbar und wird verworfen:', error);
    return null;
  }
}

export function clearSave(): void {
  storage()?.removeItem(SAVE_KEY);
}

export function loadSettings(): Settings | null {
  const raw = storage()?.getItem(SETTINGS_KEY);
  if (raw === null || raw === undefined) return null;
  try {
    return JSON.parse(raw) as Settings;
  } catch {
    return null;
  }
}

export function saveSettings(settings: Settings): void {
  try {
    storage()?.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Einstellungen sind entbehrlich.
  }
}
